import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { defineStepConfig } from "../../registry/helpers";
import { taskOutputSchema } from "./shared";

const assigneeSchema = z.object({
  type: z
    .enum(["user", "agent"])
    .describe("Whether this assignee is a user or an AI agent"),
  id: z.uuid().describe("The user ID or agent definition ID"),
  assignWhen: z
    .string()
    .optional()
    .describe("Natural-language description of when to assign to this person"),
});

export type Assignee = z.infer<typeof assigneeSchema>;

export const reassignTaskStep = defineStepConfig({
  type: "reassign_task",
  name: "Reassign Task",
  description:
    "Reassign a task to a different user or AI agent from a configured list of potential assignees",
  category: "Data Access",

  inputSchema: z.object({
    taskId: z.uuid().describe("The ID of the task to reassign"),
    assigneeId: z
      .uuid()
      .describe(
        "The ID of the new assignee (must match an entry in potentialAssignees config, or use find_task_assignees to discover valid IDs)",
      ),
    reason: z
      .string()
      .optional()
      .describe("Reason for the reassignment, stored in task metadata"),
    comment: z
      .string()
      .optional()
      .describe(
        "Markdown comment posted on the task when reassigning. Should include context for the new assignee: what was attempted, current state, and any relevant details.",
      ),
    status: z
      .enum([
        "pending",
        "in_progress",
        "needs_input",
        "failed",
        "completed",
        "cancelled",
      ])
      .optional()
      .describe(
        "Optional target status after reassignment. Defaults to pending for agents, needs_input for users.",
      ),
  }),

  outputSchema: z.union([
    z.object({
      success: z.literal(true),
      task: taskOutputSchema,
      previousAssignee: z
        .object({
          type: z.string().nullable(),
          id: z.string().nullable(),
        })
        .describe("The assignee before reassignment"),
    }),
    z.object({
      success: z.literal(false),
      error: z.string(),
    }),
  ]),

  configSchema: z.object({
    potentialAssignees: z
      .array(assigneeSchema)
      .default([])
      .describe("List of users and agents that tasks can be reassigned to"),
  }),

  requiredPermissions: [
    { action: Action.Read, subject: Subject.User },
    { action: Action.Read, subject: Subject.BackgroundAgent },
    { action: Action.Update, subject: Subject.Task },
  ],

  emits: [] as const,
});
